import type { TFunction } from 'i18next';
import { humanizeCamelCase, tradeLabel } from './tradeLabel.js';

/** The minimal shape the grouping needs off each item. */
export interface IProfessionMeta {
  tradeId?: string;
  tradeGroup?: string | null;
}

export interface IProfessionGroup<T> {
  /** Raw `tradeGroup` key (`'general'` when absent) — stable React key. */
  group: string;
  /** Localized group header (`trades.<group>` with humanized fallback). */
  groupLabel: string;
  /** Members sorted by their localized trade label. */
  items: T[];
}

/**
 * Bucket profession-like items by `tradeGroup`, sorting members by their trade
 * label and groups by their localized header — the shared ordering used by the
 * Scheduler profession picker and the Issues-page profession selector so pickers
 * list professions identically. Items lacking a `tradeGroup` fall into
 * `'general'`. `getMeta` adapts each caller's item shape to `IProfessionMeta`.
 */
export function groupProfessionsByTradeGroup<T>(
  items: readonly T[],
  getMeta: (item: T) => IProfessionMeta,
  t: TFunction,
): IProfessionGroup<T>[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const g = getMeta(item).tradeGroup ?? 'general';
    const list = map.get(g);
    if (list) list.push(item);
    else map.set(g, [item]);
  }
  return Array.from(map.entries())
    .map(([group, groupItems]) => ({
      group,
      groupLabel: t(`trades.${group}`, { defaultValue: humanizeCamelCase(group) }),
      items: [...groupItems].sort((a, b) =>
        tradeLabel(t, getMeta(a).tradeId).localeCompare(tradeLabel(t, getMeta(b).tradeId)),
      ),
    }))
    .sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
}
